import { useEffect, useRef, useState } from "react";

export interface ProofRotatorProps {
  /** The product's sentences; index 0 is the anchor shown when static. */
  proofs: readonly string[];
  /** Small caps line above the sentence, e.g. "GUESTMAKER". */
  eyebrow: string;
  /** How long each sentence stays fully visible. */
  dwellMs?: number;
  /** Crossfade duration; must match --bs-fade-ms in base.css. */
  fadeMs?: number;
  /** Show index 0 only, no rotation, no progress bar. */
  isStatic?: boolean;
}

export function ProofRotator({
  proofs,
  eyebrow,
  dwellMs = 5000,
  fadeMs = 500,
  isStatic = false,
}: ProofRotatorProps) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const timers = useRef<{ dwell?: number; fade?: number }>({});

  // Timer lifecycle: `schedule()` chains two setTimeouts per cycle (dwell,
  // then fade-out) and re-arms itself at the end, rather than a single
  // setInterval — this lets a mid-cycle visibility change swap it out
  // cleanly via `clear()` instead of desyncing an interval's phase from the
  // fade animation. Both timeout ids live in a ref (not state) since they're
  // an imperative handle, not something a re-render should react to.
  // Tab backgrounding pauses rotation entirely (`clear()` on hidden) so a
  // background tab doesn't burn through several proofs while unseen, then
  // resumes on a fresh cycle when the tab becomes visible again.
  useEffect(() => {
    if (isStatic || proofs.length < 2) return;

    const clear = () => {
      window.clearTimeout(timers.current.dwell);
      window.clearTimeout(timers.current.fade);
    };

    const schedule = () => {
      clear();
      timers.current.dwell = window.setTimeout(() => {
        setVisible(false);
        timers.current.fade = window.setTimeout(() => {
          setIndex((i) => (i + 1) % proofs.length);
          setVisible(true);
          schedule();
        }, fadeMs);
      }, dwellMs);
    };

    const onVisibility = () => {
      if (document.hidden) {
        clear();
      } else {
        setVisible(true);
        schedule();
      }
    };

    schedule();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clear();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isStatic, proofs.length, dwellMs, fadeMs]);

  const current = proofs[isStatic ? 0 : index] ?? "";

  return (
    <div className="bs-proofs" aria-hidden="true">
      <p className="bs-proofs__eyebrow">{eyebrow}</p>
      <p
        className="bs-proofs__text"
        data-visible={isStatic ? "true" : String(visible)}
      >
        {current}
      </p>
      {!isStatic && (
        // `key={index}` forces React to unmount/remount this div on every
        // rotation instead of patching its style prop in place — a CSS
        // animation does not restart on a mere property update, so without
        // the key the progress bar would freeze at 100% after the first
        // cycle instead of resetting to empty for each new sentence.
        // `animationDuration` is set inline from `dwellMs` here and read
        // back via `animation-duration: inherit` on the `::after` pseudo
        // in base.css (`bs-progress` keyframes) — that inherit is the
        // only thing keeping the bar's fill duration in sync with the
        // actual dwell time; changing one without the other desyncs them.
        <div
          key={index}
          className="bs-proofs__bar"
          style={{ animationDuration: `${dwellMs}ms` }}
        />
      )}
    </div>
  );
}
